import React, { useState, useEffect } from 'react';
import {
  Form, Input, Select, Button, Space, Card, Row, Col,
  Tooltip, Switch, message, Typography, Tabs
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, CodeOutlined, FormOutlined,
  InfoCircleOutlined, ThunderboltFilled
} from '@ant-design/icons';
import styled from 'styled-components';
import CodeEditor from 'components/shared/CodeEditor';

const { TextArea } = Input;
const { Text } = Typography;

const SimplePromptEditor = ({ value, onChange, height = '450px' }) => {
  const [form] = Form.useForm();
  const [mode, setMode] = useState('simple');
  const [guidelines, setGuidelines] = useState([]);
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [reportInstructions, setReportInstructions] = useState([{ key: 'default', content: '' }]);
  const [activeSubTab, setActiveSubTab] = useState('1');
  const [jsonCode, setJsonCode] = useState('');

  useEffect(() => {
    if (value) {
      try {
        const promptDef = typeof value === 'string' ? JSON.parse(value) : value;
        loadFromJSON(promptDef);
      } catch (error) {
        console.error('Invalid prompt definition:', error);
      }
    } else {
      setDefaults();
    }
  }, []);

  // Sync Lists/Overrides to JSON
  useEffect(() => {
    updateJSON();
  }, [guidelines, analysisTypes, reportInstructions]);

  const setDefaults = () => {
    const defaults = {
      systemRole: "You are an expert data analyst specializing in business intelligence.",
      analysisFocus: 'key_metrics',
      outputStructure: 'sections',
      tone: 'professional',
      includeVisuals: true,
      customInstructions: ''
    };

    form.setFieldsValue(defaults);
    setGuidelines([
      "Provide clear, actionable insights",
      "Use data-driven analysis",
      "Highlight trends and patterns"
    ]);
    setAnalysisTypes(['descriptive', 'diagnostic']);
    setReportInstructions([{ key: 'default', content: '' }]);
    updateJSON();
  };

  const loadFromJSON = (promptDef) => {
    form.setFieldsValue({
      systemRole: promptDef.basePrompts?.system?.role || '',
      analysisFocus: promptDef.basePrompts?.analysis?.focus || 'key_metrics',
      outputStructure: promptDef.outputFormat?.structure || 'sections',
      tone: promptDef.outputFormat?.tone || 'professional',
      includeVisuals: promptDef.outputFormat?.includeVisuals !== false,
      customInstructions: typeof promptDef.customInstructions === 'string' ? promptDef.customInstructions : '',
    });

    setGuidelines(promptDef.basePrompts?.system?.guidelines || []);
    setAnalysisTypes(promptDef.basePrompts?.analysis?.types || []);

    // Load structured custom instructions
    const instr = promptDef.customInstructions || '';
    if (typeof instr === 'object' && instr !== null) {
      const arr = Object.entries(instr).map(([k, v]) => ({ key: k, content: v }));
      setReportInstructions(arr.length > 0 ? arr : [{ key: 'default', content: '' }]);
    } else {
      setReportInstructions([{ key: 'default', content: instr || '' }]);
    }
  };

  const buildJSON = () => {
    const values = form.getFieldsValue();
    
    // Convert array back to object for storage
    const customInstructions = {};
    reportInstructions.forEach(item => {
      if (item.key) customInstructions[item.key] = item.content;
    });

    return {
      basePrompts: {
        system: {
          role: values.systemRole || '',
          guidelines: guidelines
        },
        analysis: {
          types: analysisTypes,
          focus: values.analysisFocus || 'key_metrics'
        }
      },
      outputFormat: {
        structure: values.outputStructure || 'sections',
        includeVisuals: values.includeVisuals !== false,
        tone: values.tone || 'professional'
      },
      customInstructions: customInstructions
    };
  };

  const updateJSON = () => {
    const json = buildJSON();
    setJsonCode(JSON.stringify(json, null, 2));
    onChange?.(json);
  };

  const handleFormChange = () => {
    updateJSON();
  };

  const handleJSONChange = (newCode) => {
    setJsonCode(newCode);
    try {
      const parsed = JSON.parse(newCode);
      loadFromJSON(parsed);
      onChange?.(parsed);
    } catch (error) {}
  };

  const addGuideline = () => {
    setGuidelines([...guidelines, '']);
  };

  const removeGuideline = (index) => {
    setGuidelines(guidelines.filter((_, i) => i !== index));
  };

  const updateGuideline = (index, value) => {
    const updated = [...guidelines];
    updated[index] = value;
    setGuidelines(updated);
  };

  const addReportInstruction = () => {
    setReportInstructions([...reportInstructions, { key: '', content: '' }]);
  };

  const removeReportInstruction = (index) => {
    setReportInstructions(reportInstructions.filter((_, i) => i !== index));
  };

  const updateReportInstruction = (index, field, value) => {
    const updated = [...reportInstructions];
    updated[index] = { ...updated[index], [field]: value };
    setReportInstructions(updated);
  };

  const loadTemplate = (templateType) => {
    const templates = {
      financial: {
        systemRole: "You are a financial analyst expert specializing in revenue analysis, profitability metrics, and financial forecasting.",
        guidelines: ["Focus on key financial metrics", "Identify trends in performance", "Highlight risks and opportunities"],
        analysisTypes: ['descriptive', 'diagnostic', 'predictive'],
        analysisFocus: 'key_metrics',
        outputStructure: 'executive_summary',
        tone: 'professional'
      },
      healthcare: {
         systemRole: "You are a healthcare data analyst specializing in patient outcomes, operational efficiency, and clinical metrics.",
         guidelines: ["Prioritize patient safety", "Analyze operational efficiency", "Clinical metric precision"],
         analysisTypes: ['descriptive', 'diagnostic'],
         analysisFocus: 'performance',
         outputStructure: 'sections',
         tone: 'professional'
      }
    };

    const template = templates[templateType];
    if (template) {
      form.setFieldsValue(template);
      setGuidelines(template.guidelines);
      setAnalysisTypes(template.analysisTypes);
      updateJSON();
      message.success(`${templateType.toUpperCase()} template deployed`);
    }
  };

  return (
    <EditorContainer>
      <ControlPanel>
        <ModeSwitcher>
          <SwitchButton 
            $active={mode === 'simple'} 
            onClick={() => setMode('simple')}
            icon={<FormOutlined />}
          >
            SIMPLE
          </SwitchButton>
          <SwitchButton 
            $active={mode === 'advanced'} 
            onClick={() => setMode('advanced')}
            icon={<CodeOutlined />}
          >
            ADVANCED
          </SwitchButton>
        </ModeSwitcher>

        {mode === 'simple' && (
          <TemplateRow>
            <BadgeLabel>
              <ThunderboltFilled style={{ color: '#64ffda' }} />
              PRESETS:
            </BadgeLabel>
            <Space size="small">
              <TemplateBtn size="small" onClick={() => loadTemplate('financial')}>FINANCIAL</TemplateBtn>
              <TemplateBtn size="small" onClick={() => loadTemplate('healthcare')}>HEALTHCARE</TemplateBtn>
            </Space>
          </TemplateRow>
        )}
      </ControlPanel>

      {mode === 'simple' ? (
        <FormContainer>
          <Form form={form} layout="vertical" onChange={handleFormChange}>
            <PremiumTabs 
              activeKey={activeSubTab} 
              onChange={setActiveSubTab}
              items={[
                {
                  key: '1',
                  label: 'CORE BRAIN CONFIG',
                  children: (
                    <Row gutter={[20, 20]}>
                      <Col span={24}>
                        <AuraSection title="INTELLIGENCE PERSONA">
                          <Form.Item
                            label="Define AI expertise and narrative style"
                            name="systemRole"
                            style={{ marginBottom: 0 }}
                          >
                            <PremiumTextArea
                              rows={3}
                              placeholder="e.g., You are an elite strategic advisor..."
                              maxLength={500}
                            />
                          </Form.Item>
                        </AuraSection>
                      </Col>

                      <Col span={12}>
                        <AuraSection title="ANALYSIS GUIDELINES">
                          <GuidelinesContainer>
                            {guidelines.map((guideline, index) => (
                              <GuidelineItem key={index}>
                                <PremiumInput
                                  value={guideline}
                                  onChange={(e) => updateGuideline(index, e.target.value)}
                                  placeholder="Strategic directive..."
                                />
                                <DeleteBtn
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeGuideline(index)}
                                />
                              </GuidelineItem>
                            ))}
                            <AddGuidelineBtn
                              type="dashed"
                              icon={<PlusOutlined />}
                              onClick={addGuideline}
                              block
                            >
                              ADD DIRECTIVE
                            </AddGuidelineBtn>
                          </GuidelinesContainer>
                        </AuraSection>
                      </Col>

                      <Col span={12}>
                        <AuraSection title="LOGIC & SYNTHESIS">
                          <Form.Item label="Analysis Methods" style={{ marginBottom: 16 }}>
                            <PremiumSelect
                              mode="multiple"
                              value={analysisTypes}
                              onChange={(val) => {
                                setAnalysisTypes(val);
                                // useEffect handles the sync
                              }}
                            >
                              <Select.Option value="descriptive">Descriptive</Select.Option>
                              <Select.Option value="diagnostic">Diagnostic</Select.Option>
                              <Select.Option value="predictive">Predictive</Select.Option>
                              <Select.Option value="comparative">Comparative</Select.Option>
                            </PremiumSelect>
                          </Form.Item>
                          <Row gutter={12}>
                             <Col span={12}>
                                <Form.Item label="Focus" name="analysisFocus" style={{ marginBottom: 16 }}>
                                  <PremiumSelect>
                                    <Select.Option value="key_metrics">Key Metrics</Select.Option>
                                    <Select.Option value="trends">Trends</Select.Option>
                                    <Select.Option value="strategic">Strategic</Select.Option>
                                  </PremiumSelect>
                                </Form.Item>
                             </Col>
                             <Col span={12}>
                                <Form.Item label="Tone" name="tone" style={{ marginBottom: 16 }}>
                                  <PremiumSelect>
                                    <Select.Option value="technical">Technical</Select.Option>
                                    <Select.Option value="executive">Executive</Select.Option>
                                  </PremiumSelect>
                                </Form.Item>
                             </Col>
                          </Row>
                          <Row gutter={12} align="middle">
                            <Col span={18}>
                              <Form.Item label="Layout Structure" name="outputStructure" style={{ marginBottom: 0 }}>
                                <PremiumSelect>
                                  <Select.Option value="sections">Sections</Select.Option>
                                  <Select.Option value="executive_summary">Executive Summary</Select.Option>
                                  <Select.Option value="detailed">Detailed Analysis</Select.Option>
                                </PremiumSelect>
                              </Form.Item>
                            </Col>
                            <Col span={6} style={{ textAlign: 'center' }}>
                               <Form.Item label="Visuals" name="includeVisuals" valuePropName="checked" style={{ marginBottom: 0 }}>
                                <Switch size="small" />
                              </Form.Item>
                            </Col>
                          </Row>
                        </AuraSection>
                      </Col>
                    </Row>
                  )
                },
                {
                  key: '2',
                  label: 'PAGE-SPECIFIC INTELLIGENCE',
                  children: (
                    <AuraSection title="CUSTOM REPORT INSTRUCTIONS">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <InstructionGrid>
                          {reportInstructions.map((item, index) => (
                            <PremiumInstructionCard key={index}>
                              <CardHeader>
                                <Input 
                                  placeholder="Page Name (e.g. Mortality Exposure)" 
                                  value={item.key} 
                                  onChange={(e) => updateReportInstruction(index, 'key', e.target.value)}
                                  className="page-name-input"
                                />
                                <DeleteBtn 
                                  size="small"
                                  type="text" 
                                  danger 
                                  icon={<DeleteOutlined />} 
                                  onClick={() => removeReportInstruction(index)} 
                                />
                              </CardHeader>
                              <PremiumTextArea
                                rows={8}
                                value={item.content}
                                onChange={(e) => updateReportInstruction(index, 'content', e.target.value)}
                                placeholder="Instructions for this specific page (Analytical framework, escalation triggers, etc.)"
                                className="instruction-textarea"
                              />
                            </PremiumInstructionCard>
                          ))}
                        </InstructionGrid>
                        <AddPageBtn 
                          type="dashed" 
                          icon={<PlusOutlined />} 
                          onClick={addReportInstruction}
                        >
                          ADD NEW PAGE INTELLIGENCE OVERRIDE
                        </AddPageBtn>
                      </div>
                    </AuraSection>
                  )
                }
              ]}
            />
          </Form>
        </FormContainer>
      ) : (
        <CodeWrapper>
           <HelpBar>
            <InfoCircleOutlined style={{ color: 'var(--aura-navy)' }} />
            <Text style={{ fontSize: 11, color: 'var(--aura-slate)', marginLeft: 8, fontWeight: 700 }}>
              DIRECT LOGIC INJECTION ENABLED.
            </Text>
          </HelpBar>
          <CodeEditor
            language="json"
            value={jsonCode}
            onChange={handleJSONChange}
            height={height}
          />
        </CodeWrapper>
      )}
    </EditorContainer>
  );
};

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: 'Inter', sans-serif;
`;

const ControlPanel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
`;

const ModeSwitcher = styled.div`
  display: flex;
  background: #f1f5f9;
  padding: 2px;
  border-radius: 6px;
`;

const SwitchButton = styled(Button)`
  height: 28px !important;
  padding: 0 16px !important;
  border-radius: 4px !important;
  border: none !important;
  font-weight: 800 !important;
  font-size: 10px !important;
  background: ${props => props.$active ? 'white' : 'transparent'} !important;
  color: ${props => props.$active ? 'var(--aura-navy)' : 'var(--aura-slate)'} !important;
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'} !important;
`;

const TemplateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BadgeLabel = styled.div`
  font-size: 9px;
  font-weight: 900;
  color: var(--aura-slate);
  letter-spacing: 0.8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TemplateBtn = styled(Button)`
  font-size: 9px !important;
  font-weight: 700 !important;
  border-radius: 6px !important;
  height: 24px !important;
  border-color: #f1f5f9 !important;
`;

const AuraSection = styled(Card)`
  border-radius: 8px !important;
  border: 1px solid #f1f5f9 !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.01) !important;
  
  .ant-card-head {
    min-height: 40px !important;
    border-bottom: 1px solid #f1f5f9 !important;
    padding: 0 12px !important;
  }
  .ant-card-head-title {
    font-size: 9px !important;
    font-weight: 850 !important;
    letter-spacing: 0.8px !important;
    color: var(--aura-slate) !important;
    text-transform: uppercase;
  }
  .ant-card-body { padding: 16px !important; }
`;

const FormContainer = styled.div`
  animation: fadeIn 0.3s ease-out;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const PremiumTextArea = styled(TextArea)`
  border-radius: 6px !important;
  border-color: #f1f5f9 !important;
  font-size: 13px !important;
`;

const GuidelinesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const GuidelineItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const PremiumInput = styled(Input)`
  border-radius: 6px !important;
  height: 36px !important;
  border-color: #f1f5f9 !important;
  font-size: 13px !important;
`;

const DeleteBtn = styled(Button)`
  width: 32px !important;
  height: 32px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: #fff1f0 !important;
  border: none !important;
  border-radius: 4px !important;
`;

const AddGuidelineBtn = styled(Button)`
  height: 36px !important;
  border-radius: 6px !important;
  font-weight: 800 !important;
  font-size: 10px !important;
  border-style: dashed !important;
`;

const PremiumSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    border-radius: 6px !important;
    height: 36px !important;
    border-color: #f1f5f9 !important;
    display: flex !important;
    align-items: center !important;
  }
`;

const CodeWrapper = styled.div`
  background: white;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
`;

const PremiumTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px !important;
    background: white;
    padding: 0 16px;
    border-radius: 8px;
    border: 1px solid #f1f5f9;
  }
  .ant-tabs-tab {
    padding: 12px 0 !important;
    font-size: 11px !important;
    font-weight: 850 !important;
    letter-spacing: 0.8px !important;
    color: var(--aura-slate) !important;
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--aura-navy) !important;
  }
  .ant-tabs-ink-bar {
    background: var(--aura-navy) !important;
    height: 3px !important;
  }
`;

const InstructionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
  gap: 20px;
`;

const PremiumInstructionCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s ease;
  &:hover {
    border-color: var(--aura-navy);
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }
  .instruction-textarea {
    border: 1px solid #e2e8f0 !important;
    background: white !important;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px !important;
    line-height: 1.6;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  .page-name-input {
    border: none !important;
    background: transparent !important;
    font-weight: 900;
    font-size: 13px;
    color: var(--aura-navy);
    padding: 0 !important;
    &:focus { box-shadow: none !important; }
  }
`;

const AddPageBtn = styled(Button)`
  height: 48px !important;
  border-radius: 12px !important;
  font-weight: 800 !important;
  font-size: 11px !important;
  letter-spacing: 0.5px !important;
  border-style: dashed !important;
  border-color: #cbd5e1 !important;
`;

const HelpBar = styled.div`
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
`;

export default SimplePromptEditor;


